import styled from 'styled-components';
import { Button } from 'antd';

export const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  background: ${props => props.theme['@component-background'] || '#ffffff'};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

export const TopScrollBar = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 11;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  background: ${props => props.theme['@component-background'] || '#ffffff'};

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
`;

export const EmptyState = styled.div`
  padding: 60px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

export const StyledTableHead = styled.thead`
  background: ${props => props.theme['@table-header-bg'] || '#fafafa'};
  position: sticky;
  top: 0;
  z-index: 2;
`;

export const StyledTableRow = styled.tr`
  border-bottom: 1px solid #d9d9d9;

  &:hover {
    background: ${props => props.theme['@table-row-hover-bg']};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const StyledTableHeader = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme['@text-color'] || 'rgba(0, 0, 0, 0.85)'};
  background: ${props => props.theme['@table-header-bg'] || '#fafafa'};
  border-bottom: 1px solid
    ${props => props.theme['@border-color-base'] || '#d9d9d9'};
  white-space: nowrap;
  user-select: none;
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${props => (props.canSort ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    color: ${props =>
      props.canSort ? props.theme['@primary-color'] : 'inherit'};
  }
`;

export const SortIndicator = styled.span`
  display: inline-flex;
  flex-direction: column;
  font-size: 10px;
  vertical-align: middle;
  flex-shrink: 0;

  .sort-arrow-up {
    margin-bottom: -2px;
    color: ${props =>
      props.direction === 'asc' ? '#47a4ea' : 'rgba(0, 0, 0, 0.25)'};
  }

  .sort-arrow-down {
    color: ${props =>
      props.direction === 'desc' ? '#47a4ea' : 'rgba(0, 0, 0, 0.25)'};
  }
`;

export const StyledTableBody = styled.tbody``;

export const StyledTableCell = styled.td`
  padding: 16px;
  color: ${props => props.theme['@text-color']};
  overflow-wrap: break-word;
`;
// border-bottom: 1px solid ${props => props.theme['@border-color-split']};

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #d9d9d9;
  background: ${props => props.theme['@component-background']};
`;

export const PaginationInfo = styled.div`
  color: ${props => props.theme['@text-color-secondary']};
  font-size: 14px;
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    color: ${props => props.theme['@text-color']};
    margin: 0 8px;
    font-size: 14px;
  }
`;

export const DraggableHeader = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme['@text-color'] || 'rgba(0, 0, 0, 0.85)'};
  background: ${props => props.theme['@table-header-bg'] || '#fafafa'};
  border-bottom: 1px solid
    ${props => props.theme['@border-color-base'] || '#d9d9d9'};
  white-space: nowrap;
  user-select: none;
  position: relative;
  overflow: hidden;

  ${props => props.isDragging && `opacity: 0.5;`}
  ${props => props.isOver && `border-left: 2px solid #47a4ea;`}
  ${props => props.draggable && `cursor: grab;`}
`;

export const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 10px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 1;

  &:hover {
    background: rgba(71, 164, 234, 0.3);
  }

  ${props =>
    props.isResizing &&
    `
    background: rgba(71, 164, 234, 0.5);
  `}
`;

export const PaginationButton = styled(Button)`
  &&& {
    min-width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: ${props => props.theme['@text-color']};

    &:hover:not(:disabled) {
      color: ${props => props.theme['@primary-color']};
      background: ${props => props.theme['@primary-1']};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;
